import moment from 'moment-timezone';
import { apiGet, apiPost } from './apiService';
import {
  listUtilizationTemplates,
  upsertUtilizationTemplate,
  getUtilizationTemplate,
  saveUtilizationDayData,
  listUtilizationFacts,
  listUtilizationEvents,
  saveFlightImportBatch,
  listFlightSchedules,
  listFlightImportBatches,
  upsertFlightBayMappings as dbUpsertFlightBayMappings,
  listFlightBayMappings as dbListFlightBayMappings,
} from './utilizationDB';
import { createStarterTemplates, formatDuration } from './utilizationDefaults';
import { computeAssetUtilization, extractRawRows, normalizeRawPoints, toUtcRangeForDateInTz } from './utilizationEngine';
import { fetchDailyFuelDataReport, fetchIotDeviceDataInPeriod, getDeviceIdFromAsset } from './fuelService';
import { getUserData } from './authService';
import {
  parseFlightScheduleFile,
  computeFlightContextFromData,
  buildEffectiveBayMappings,
  normalizeBayKey,
} from './flightContextEngine';

const storageAssetKey = 'utilization_assets_cache_v1';
const flightContextMemo = new Map();
let flightContextCacheNonce = 0;

const invalidateFlightContextCache = () => {
  flightContextCacheNonce += 1;
  flightContextMemo.clear();
};

const readAssetCache = () => {
  try {
    const raw = localStorage.getItem(storageAssetKey);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
};

const saveAssetCache = (assets) => {
  try {
    localStorage.setItem(storageAssetKey, JSON.stringify(assets));
  } catch (e) {
    console.warn('Could not cache assets:', e);
  }
};

const toNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const computeContextFromRawPoints = (points) => {
  if (!Array.isArray(points) || points.length < 2) {
    return {
      ignitionTimeSec: 0,
      distanceKm: 0,
      fuelConsumed: 0,
    };
  }

  let ignitionTimeSec = 0;
  let haversineDistanceMeters = 0;
  let firstOdo = null;
  let lastOdo = null;
  let firstFuelConsGnss = null;
  let lastFuelConsGnss = null;

  for (let i = 1; i < points.length; i += 1) {
    const prev = points[i - 1];
    const curr = points[i];
    const deltaSec = Math.max(0, Math.round((curr.ts - prev.ts) / 1000));

    if (prev.ignition === 1 && deltaSec <= 300) {
      ignitionTimeSec += deltaSec;
    }

    // Local haversine estimate to avoid extra summary APIs
    const R = 6371000;
    const dLat = ((curr.lat - prev.lat) * Math.PI) / 180;
    const dLng = ((curr.lng - prev.lng) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((prev.lat * Math.PI) / 180) *
        Math.cos((curr.lat * Math.PI) / 180) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    haversineDistanceMeters += R * c;
  }

  points.forEach((p) => {
    const odo = toNumber(p.odometer);
    if (odo > 0 && firstOdo === null) firstOdo = odo;
    if (odo > 0) lastOdo = odo;

    const fuelConsGnss = toNumber(p.raw?.fuel_consumption_GNSS ?? p.raw?.fuelConsumptionGNSS);
    if (fuelConsGnss > 0 && firstFuelConsGnss === null) firstFuelConsGnss = fuelConsGnss;
    if (fuelConsGnss > 0) lastFuelConsGnss = fuelConsGnss;
  });

  const odoDistanceKm =
    firstOdo !== null && lastOdo !== null && lastOdo >= firstOdo ? lastOdo - firstOdo : 0;

  const distanceKm = odoDistanceKm > 0 ? odoDistanceKm : haversineDistanceMeters / 1000;

  // fuel_consumption_GNSS is typically in ml in many device payloads; convert to liters
  let fuelConsumed = 0;
  if (
    firstFuelConsGnss !== null &&
    lastFuelConsGnss !== null &&
    lastFuelConsGnss >= firstFuelConsGnss
  ) {
    fuelConsumed = (lastFuelConsGnss - firstFuelConsGnss) / 1000;
  }

  return {
    ignitionTimeSec,
    distanceKm,
    fuelConsumed,
  };
};

const getAssetFromDailyRow = (row) => {
  if (!row || !row.asset) return null;
  const asset = row.asset;
  return {
    ...asset,
    _id: asset._id,
    assetCode: asset.assetCode,
    location: asset.location || null,
    client: asset.client || null,
    _dailyDeviceId: row.device_id || null,
    _dailyRow: row,
  };
};

export const ensureStarterTemplates = () => {
  const existing = listUtilizationTemplates();
  if (existing.length > 0) return existing;

  const user = getUserData();
  const orgId = typeof user?.organisation === 'string'
    ? user.organisation
    : (user?.organisation?.name || user?.organisation?._id || 'default-org');
  const createdBy = user?.email || 'system';
  const starters = createStarterTemplates(orgId, createdBy);
  starters.forEach((t) => upsertUtilizationTemplate(t));
  return listUtilizationTemplates();
};

export const saveTemplate = async (template) => {
  try {
    const remote = await apiPost('/utilization/templates', template);
    if (remote.success && remote.data) {
      const saved = upsertUtilizationTemplate(remote.data);
      invalidateFlightContextCache();
      return saved;
    }
  } catch (e) {
    console.warn('[utilization] remote save failed, using local:', e);
  }

  const saved = upsertUtilizationTemplate(template);
  invalidateFlightContextCache();
  return saved;
};

export const fetchTemplates = async () => {
  try {
    const remote = await apiGet('/utilization/templates');
    if (remote.success && Array.isArray(remote.data) && remote.data.length > 0) {
      remote.data.forEach((t) => upsertUtilizationTemplate(t));
      return listUtilizationTemplates();
    }
  } catch (e) {
    console.warn('[utilization] remote list failed, using local:', e);
  }

  return ensureStarterTemplates();
};

export const fetchAllAssets = async () => {
  try {
    const res = await apiGet('/asset/asset-all', {
      headers: {
        Accept: '*/*',
      },
    });

    if (res.success && Array.isArray(res.data)) {
      saveAssetCache(res.data);
      return res.data;
    }
  } catch (e) {
    console.warn('[utilization] fetchAllAssets failed:', e);
  }

  return readAssetCache();
};

export const fetchAssetsForDate = async ({ date, templateId = null }) => {
  const dateMoment = moment(date);
  const startTs = dateMoment.clone().startOf('day').valueOf();
  const endTs = dateMoment.clone().endOf('day').valueOf();

  try {
    const daily = await fetchDailyFuelDataReport(startTs, endTs, {
      assets: null,
      locations: ['all'],
      subLocations: ['all'],
      frequency: 'Daily',
    });

    if (daily.success && Array.isArray(daily.data)) {
      const mapped = daily.data.map(getAssetFromDailyRow).filter(Boolean);
      const unique = [];
      const seen = new Set();
      mapped.forEach((asset) => {
        if (!asset?._id || seen.has(asset._id)) return;
        seen.add(asset._id);
        unique.push(asset);
      });
      if (unique.length > 0) {
        saveAssetCache(unique);
        return unique;
      }
    }
  } catch (e) {
    console.warn('[utilization] fetchAssetsForDate failed:', e);
  }

  return fetchAllAssets();
};

export const importFlightScheduleForTemplate = async ({ templateId, file, timezone }) => {
  const template = getUtilizationTemplate(templateId);
  if (!template) return { success: false, error: 'Template not found' };
  if (!file) return { success: false, error: 'Flight file is required' };

  try {
    const effectiveTz = timezone || template.timezone || moment.tz.guess() || 'Asia/Kolkata';
    const parsed = await parseFlightScheduleFile({
      file,
      templateId,
      timezone: effectiveTz,
    });

    const replaceLocalDates = Array.from(new Set((parsed.flights || []).map((row) => row.localDate).filter(Boolean)));
    const batch = {
      id: parsed.importBatchId,
      templateId,
      sourceFileName: parsed.fileName,
      timezone: effectiveTz,
      rowsParsed: parsed.summary?.rowsParsed || 0,
      validRows: parsed.summary?.validRows || 0,
      rejectedRows: parsed.summary?.rejectedRows || 0,
      dateFrom: parsed.summary?.dateFrom || null,
      dateTo: parsed.summary?.dateTo || null,
      airports: parsed.summary?.airports || [],
      bayKeys: parsed.summary?.bayKeys || [],
      bayKeyCount: parsed.summary?.bayKeyCount || 0,
      importedAt: new Date().toISOString(),
    };

    const persisted = saveFlightImportBatch({
      templateId,
      batch,
      flights: parsed.flights || [],
      replaceLocalDates,
    });

    const manualMappings = dbListFlightBayMappings({ templateId });
    const mappingPreview = buildEffectiveBayMappings({
      bayKeys: parsed.summary?.bayKeys || [],
      zones: template.zones || [],
      manualMappings,
    });

    invalidateFlightContextCache();

    return {
      success: true,
      data: {
        batch,
        persisted,
        summary: parsed.summary,
        rejectedRows: parsed.rejected,
        mappingPreview: mappingPreview.rows,
      },
    };
  } catch (error) {
    return { success: false, error: error.message || 'Flight import failed' };
  }
};

export const listFlightImports = async ({ templateId, dateFrom, dateTo }) => {
  const rows = listFlightSchedules({ templateId, dateFrom, dateTo });
  const batches = listFlightImportBatches({ templateId });
  const airports = Array.from(new Set(rows.map((r) => r.airportName).filter(Boolean))).sort();
  const bayKeys = Array.from(new Set(rows.flatMap((r) => r.bayKeys || []))).sort();
  const dateKeys = Array.from(new Set(rows.map((r) => r.localDate).filter(Boolean))).sort();

  return {
    success: true,
    data: {
      rows,
      batches,
      summary: {
        rows: rows.length,
        airports,
        bayKeys,
        dateFrom: dateKeys[0] || null,
        dateTo: dateKeys[dateKeys.length - 1] || null,
      },
    },
  };
};

export const getFlightBayMappings = async ({ templateId }) => {
  return {
    success: true,
    data: dbListFlightBayMappings({ templateId }),
  };
};

export const upsertFlightBayMappings = async ({ templateId, mappings }) => {
  const normalized = (mappings || []).map((entry) => ({
    ...entry,
    bayKey: normalizeBayKey(entry?.bayKey),
  })).filter((entry) => entry.bayKey);

  const saved = dbUpsertFlightBayMappings({
    templateId,
    mappings: normalized,
  });

  invalidateFlightContextCache();

  return {
    success: true,
    data: saved,
  };
};

export const computeFlightContextReport = async ({ templateId, date, assetIds = [], channelMode = 'polygon_priority' }) => {
  const template = getUtilizationTemplate(templateId);
  if (!template) return { success: false, error: 'Template not found' };

  const timezone = template.timezone || moment.tz.guess() || 'Asia/Kolkata';
  const dateKey = moment.tz(date, timezone).format('YYYY-MM-DD');

  const flights = listFlightSchedules({ templateId, dateFrom: dateKey, dateTo: dateKey });
  const manualMappings = dbListFlightBayMappings({ templateId });
  const eventsAll = listUtilizationEvents({ templateId, date: dateKey });
  const events = Array.isArray(assetIds) && assetIds.length > 0
    ? eventsAll.filter((event) => assetIds.includes(event.assetId))
    : eventsAll;
  const importBatches = listFlightImportBatches({ templateId });

  const importBatchVersion = latestVersionStamp(importBatches, 'importedAt');
  const mappingVersion = latestVersionStamp(manualMappings, 'updatedAt');
  const eventVersion = events.length > 0
    ? `${events.length}_${events[events.length - 1]?.startTs || 0}`
    : '0';
  const cacheKey = [
    templateId,
    dateKey,
    [...(assetIds || [])].sort().join(','),
    importBatchVersion,
    mappingVersion,
    Number(template?.rules?.dwellThresholdSec || 120),
    template.updatedAt || '',
    eventVersion,
    channelMode,
    flightContextCacheNonce,
  ].join('|');

  if (flightContextMemo.has(cacheKey)) {
    return {
      success: true,
      data: JSON.parse(JSON.stringify(flightContextMemo.get(cacheKey))),
      cached: true,
    };
  }

  const computed = computeFlightContextFromData({
    template,
    dateKey,
    events,
    flights,
    manualMappings,
    assetIds,
    channelMode,
  });

  const airports = Array.from(new Set(flights.map((f) => f.airportName).filter(Boolean))).sort();

  const data = {
    ...computed,
    dateKey,
    airports,
    importSummary: {
      totalRows: flights.length,
      airports,
      date: dateKey,
      lastImportAt: importBatchVersion && importBatchVersion !== 'none' ? importBatchVersion : null,
    },
  };

  flightContextMemo.set(cacheKey, data);

  return {
    success: true,
    data: JSON.parse(JSON.stringify(data)),
    cached: false,
  };
};

const filterAssetsByTemplateScope = (assets, template) => {
  const scope = template.scope || {};
  return assets.filter((asset) => {
    if (scope.assetIds?.length > 0 && !scope.assetIds.includes(asset._id)) return false;

    if (scope.locations?.length > 0) {
      const locationCode = typeof asset.location === 'object' ? asset.location?.code : asset.location;
      if (!scope.locations.includes(locationCode)) return false;
    }

    if (scope.subLocations?.length > 0) {
      const subLocationCode = typeof asset.client === 'object' ? asset.client?.code : asset.client;
      if (!scope.subLocations.includes(subLocationCode)) return false;
    }

    return true;
  });
};

const latestVersionStamp = (rows, dateKey = 'updatedAt') => {
  if (!Array.isArray(rows) || rows.length === 0) return 'none';
  return rows.reduce((latest, row) => {
    const candidate = row?.[dateKey] || row?.updatedAt || row?.importedAt || '';
    return `${candidate}` > `${latest}` ? `${candidate}` : `${latest}`;
  }, '');
};

const computeForAsset = async ({ template, asset, startTs, endTs, dateKey, dailyRow }) => {
  let deviceId = dailyRow?.device_id || asset?._dailyDeviceId || null;
  if (!deviceId) {
    const deviceResult = await getDeviceIdFromAsset(asset.assetCode, asset._id);
    deviceId = deviceResult.success ? deviceResult.deviceId : null;
  }

  let normalizedPoints = [];
  if (deviceId) {
    const rawResult = await fetchIotDeviceDataInPeriod(deviceId, startTs, endTs, asset._id);
    if (rawResult.success) {
      const rows = extractRawRows(rawResult.data);
      normalizedPoints = normalizeRawPoints(rows);
    }
  }

  const contextMetrics = computeContextFromRawPoints(normalizedPoints);

  const computation = computeAssetUtilization({
    template,
    asset,
    points: normalizedPoints,
    contextMetrics,
    dateKey,
  });

  return {
    ...computation,
    diagnostics: {
      pointCount: normalizedPoints.length,
      hasDeviceId: !!deviceId,
    },
  };
};

export const runUtilizationCompute = async ({ templateId, date, assetIds = [] }) => {
  const template = getUtilizationTemplate(templateId);
  if (!template) {
    return { success: false, error: 'Template not found' };
  }

  const timezone = template.timezone || moment.tz.guess() || 'Asia/Kolkata';
  const { startTs, endTs, dateKey } = toUtcRangeForDateInTz(date, timezone);

  try {
    const remote = await apiPost(`/utilization/compute/${startTs}/${endTs}`, {
      templateId,
      timezone,
    });

    if (remote.success) {
      invalidateFlightContextCache();
      return { success: true, data: remote.data, source: 'remote' };
    }
  } catch (e) {
    console.warn('[utilization] remote compute failed, using local:', e);
  }

  const allAssets = await fetchAllAssets();
  const scopedAssets = filterAssetsByTemplateScope(allAssets, template);

  const allEvents = [];
  const allFacts = [];
  const diagnostics = [];
  const maxAssetsWithoutFilter = 20;
  let assetsToProcess = scopedAssets;
  if (Array.isArray(assetIds) && assetIds.length > 0) {
    const allowed = new Set(assetIds);
    assetsToProcess = assetsToProcess.filter((a) => allowed.has(a._id));
  }

  if (assetsToProcess.length > maxAssetsWithoutFilter) {
    assetsToProcess = assetsToProcess.slice(0, maxAssetsWithoutFilter);
  }

  for (const asset of assetsToProcess) {
    try {
      const computed = await computeForAsset({
        template,
        asset,
        startTs,
        endTs,
        dateKey,
        dailyRow: null,
      });

      allEvents.push(...computed.events);
      allFacts.push(...computed.facts);
      diagnostics.push({
        assetId: asset._id,
        assetCode: asset.assetCode,
        ...computed.diagnostics,
      });
    } catch (error) {
      diagnostics.push({
        assetId: asset._id,
        assetCode: asset.assetCode,
        error: error.message,
      });
    }
  }

  saveUtilizationDayData({
    templateId,
    dateKey,
    events: allEvents,
    facts: allFacts,
    runMeta: {
      timezone,
      source: 'local',
      assetCount: assetsToProcess.length,
      requestedAssetCount: scopedAssets.length,
      capped: scopedAssets.length > assetsToProcess.length,
      diagnostics,
    },
  });

  invalidateFlightContextCache();

  return {
    success: true,
    source: 'local',
    data: {
      templateId,
      dateKey,
      eventsStored: allEvents.length,
      factsStored: allFacts.length,
      assetCount: assetsToProcess.length,
      requestedAssetCount: scopedAssets.length,
      capped: scopedAssets.length > assetsToProcess.length,
      diagnostics,
    },
  };
};

const buildRows = (facts) => {
  const byAsset = {};

  facts.forEach((fact) => {
    const rowKey = `${fact.assetId}_${fact.date}`;
    if (!byAsset[rowKey]) {
      byAsset[rowKey] = {
        key: rowKey,
        date: fact.date,
        assetId: fact.assetId,
        assetCode: fact.assetCode,
      };
    }

    byAsset[rowKey][fact.metricKey] = fact.metricValue;
  });

  return Object.values(byAsset);
};

const aggregateFacts = (facts) => {
  const agg = {};
  const units = {};

  facts.forEach((f) => {
    agg[f.metricKey] = (agg[f.metricKey] || 0) + (Number(f.metricValue) || 0);
    units[f.metricKey] = f.unit;
  });

  return {
    values: agg,
    units,
  };
};

export const fetchUtilizationReport = async ({ templateId, dateFrom, dateTo, assetIds = [], location, subLocation }) => {
  const flightContextForDate = async () => computeFlightContextReport({
    templateId,
    date: dateTo,
    assetIds,
    channelMode: 'polygon_priority',
  });

  try {
    const query = new URLSearchParams({
      templateId,
      dateFrom,
      dateTo,
    });
    if (assetIds.length > 0) query.set('assetIds', assetIds.join(','));
    if (location) query.set('location', location);
    if (subLocation) query.set('subLocation', subLocation);

    const remote = await apiGet(`/utilization/report?${query.toString()}`);
    if (remote.success && remote.data) {
      const flightContext = await flightContextForDate();
      return {
        success: true,
        data: {
          ...remote.data,
          flightContext: flightContext.success ? flightContext.data : null,
        },
        source: 'remote',
      };
    }
  } catch (e) {
    console.warn('[utilization] remote report failed, using local:', e);
  }

  const template = getUtilizationTemplate(templateId);
  if (!template) return { success: false, error: 'Template not found' };

  const facts = listUtilizationFacts({ templateId, dateFrom, dateTo, assetIds });
  const rows = buildRows(facts);
  const aggregates = aggregateFacts(facts);

  const currentDateKey = moment.tz(dateTo, template.timezone || moment.tz.guess() || 'Asia/Kolkata').format('YYYY-MM-DD');
  const prevDateKey = moment.tz(currentDateKey, template.timezone || moment.tz.guess() || 'Asia/Kolkata').subtract(1, 'day').format('YYYY-MM-DD');

  const currentFacts = facts.filter((f) => f.date === currentDateKey);
  const prevFacts = listUtilizationFacts({ templateId, dateFrom: prevDateKey, dateTo: prevDateKey, assetIds });
  const currentAgg = aggregateFacts(currentFacts);
  const prevAgg = aggregateFacts(prevFacts);

  const comparison = {};
  Object.keys(currentAgg.values).forEach((metricKey) => {
    const current = currentAgg.values[metricKey] || 0;
    const prev = prevAgg.values[metricKey] || 0;
    comparison[metricKey] = {
      current,
      previous: prev,
      delta: current - prev,
      deltaPct: prev !== 0 ? ((current - prev) / prev) * 100 : null,
      unit: currentAgg.units[metricKey] || prevAgg.units[metricKey],
      formattedCurrent: currentAgg.units[metricKey] === 'sec' ? formatDuration(current) : current,
      formattedPrevious: prevAgg.units[metricKey] === 'sec' ? formatDuration(prev) : prev,
    };
  });

  const flightContext = await flightContextForDate();

  return {
    success: true,
    source: 'local',
    data: {
      template,
      rows,
      aggregates,
      comparison,
      facts,
      flightContext: flightContext.success ? flightContext.data : null,
    },
  };
};

export const fetchUtilizationEvents = async ({ templateId, assetId, date }) => {
  try {
    const query = new URLSearchParams({ templateId, assetId, date });
    const remote = await apiGet(`/utilization/events?${query.toString()}`);
    if (remote.success && Array.isArray(remote.data)) {
      return { success: true, data: remote.data, source: 'remote' };
    }
  } catch (e) {
    console.warn('[utilization] remote events failed, using local:', e);
  }

  return {
    success: true,
    source: 'local',
    data: listUtilizationEvents({ templateId, assetId, date }),
  };
};

export const fetchUtilizationTrack = async ({ templateId, assetId, date }) => {
  const template = getUtilizationTemplate(templateId);
  if (!template) return { success: false, error: 'Template not found', data: [] };

  const timezone = template.timezone || moment.tz.guess() || 'Asia/Kolkata';
  const { startTs, endTs } = toUtcRangeForDateInTz(date, timezone);

  const assets = await fetchAllAssets();
  const asset = assets.find((a) => a._id === assetId);
  if (!asset) return { success: false, error: 'Asset not found', data: [] };

  let deviceId = asset?._dailyDeviceId || null;
  if (!deviceId) {
    const deviceResult = await getDeviceIdFromAsset(asset.assetCode, asset._id);
    if (!deviceResult.success || !deviceResult.deviceId) {
      return { success: false, error: deviceResult.error || 'Device not found for asset', data: [] };
    }
    deviceId = deviceResult.deviceId;
  }

  const rawResult = await fetchIotDeviceDataInPeriod(deviceId, startTs, endTs, asset._id);
  if (!rawResult.success) {
    return { success: false, error: rawResult.error || 'Failed to fetch track data', data: [] };
  }

  const rows = extractRawRows(rawResult.data);
  const points = normalizeRawPoints(rows);

  return {
    success: true,
    data: points,
    meta: {
      startTs,
      endTs,
      timezone,
      deviceId,
      assetId: asset._id,
      assetCode: asset.assetCode,
    },
  };
};
